import { DEALERS_URL, DEVICES_API_URL, DEVICES_FILTER_URL, DEVICES_REDUCE_URL, DEVICES_URL, GROUPS_URL } from 'constants/ApiConstants';
import { MAX_RECORD } from 'constants/Global';
import * as types from 'modules/devices/actionTypes';
import type * as DeviceType from 'modules/devices/types';
import type { ApiError, MetaAction, PageContent } from 'modules/global/generic.types';

export const fetchDevices = (params: Record<string, unknown>, meta: MetaAction = {}): DeviceType.FetchDevicesAction => ({
  type: types.FETCH_DEVICES_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: DEVICES_FILTER_URL,
      params: {
        projection: 'page',
        ...params,
      },
    },
  },
  meta,
});

export const fetchDevicesSuccess = (devices: PageContent<DeviceType.Device>, meta: MetaAction = {}): DeviceType.FetchDevicesSuccessAction => ({
  type: types.FETCH_DEVICES_SUCCESS,
  payload: devices,
  meta,
});

export const createDeviceRequest = (device: Partial<DeviceType.Device>, meta: MetaAction = {}): DeviceType.CreateDeviceAction => ({
  type: types.CREATE_DEVICE_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: DEVICES_API_URL,
      data: device,
    },
  },
  meta,
});

export const createDeviceFailure = (message: string, meta: MetaAction = {}): DeviceType.CreateDeviceFailureAction => ({
  type: types.CREATE_DEVICE_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const deleteDeviceRequest = (
  device: { id: string },
  params?: Record<string, unknown>,
  meta: MetaAction = {},
): DeviceType.DeleteDeviceAction => ({
  type: types.DELETE_DEVICE_REQUEST,
  payload: {
    request: {
      method: 'DELETE',
      url: `${DEVICES_API_URL}/${device.id}`,
      params,
    },
  },
  meta,
});

export const deviceNameCheckRequest = ({
  name,
  dealerId,
  groupId,
}: {
  name: string;
  dealerId?: string;
  groupId?: string;
}, meta: MetaAction = {}): DeviceType.DeviceNameCheckAction => {
  const baseURL = dealerId ? `${DEALERS_URL}/${dealerId}` : `${GROUPS_URL}/${groupId}`;

  return {
    type: types.DEVICE_NAME_CHECK_REQUEST,
    payload: {
      request: {
        method: 'HEAD',
        url: `${baseURL}/devices/name`,
        params: { name },
      },
    },
    meta,
  };
};

export const deviceNameCheckAbortRequest = (meta: MetaAction = {}): DeviceType.DeviceNameCheckAbortAction => ({
  type: types.DEVICE_NAME_CHECK_ABORT,
  payload: undefined,
  meta,
});

export const deviceNameCheckSuccess = (meta: MetaAction = {}): DeviceType.DeviceNameCheckSuccessAction => ({
  type: types.DEVICE_NAME_CHECK_SUCCESS,
  meta,
});

export const deviceNameCheckFailure = (error: string, meta: MetaAction = {}): DeviceType.DeviceNameCheckFailureAction => ({
  type: types.DEVICE_NAME_CHECK_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const sendByEmail = ({ deviceId, email }: { deviceId: string; email: string }, meta: MetaAction = {}): DeviceType.SendByEmailAction => ({
  type: types.SEND_BY_EMAIL,
  payload: {
    request: {
      method: 'POST',
      url: `${DEVICES_API_URL}/${deviceId}/qrCode/send`,
      data: { email },
    },
  },
  meta,
});

export const fetchDevice = (deviceId: string, meta: MetaAction = {}): DeviceType.FetchDeviceAction => ({
  type: types.FETCH_DEVICE,
  payload: {
    request: {
      method: 'GET',
      url: `${DEVICES_URL}/${deviceId}`,
      params: {
        projection: 'details',
      },
    },
    deviceId,
  },
  meta,
});

export const fetchDeviceSuccess = (device: DeviceType.Device, meta: MetaAction = {}): DeviceType.FetchDeviceSuccessAction => ({
  type: types.FETCH_DEVICE_SUCCESS,
  payload: device,
  meta,
});

export const patchDevice = (device: Partial<DeviceType.Device> & { id: string }, meta: MetaAction = {}): DeviceType.PatchDeviceAction => ({
  type: types.PATCH_DEVICE,
  payload: {
    request: {
      method: 'PATCH',
      url: `${DEVICES_API_URL}/${device.id}`,
      data: device,
    },
  },
  meta,
});

export const patchDeviceSuccess = (device?: DeviceType.Device, meta: MetaAction = {}): DeviceType.PatchDeviceSuccessAction => ({
  type: types.PATCH_DEVICE_SUCCESS,
  payload: device,
  meta,
});

export const patchDeviceFailure = (error: string, meta: MetaAction = {}): DeviceType.PatchDeviceFailureAction => ({
  type: types.PATCH_DEVICE_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const selectDevice = (deviceId: string, meta: MetaAction = {}): DeviceType.SelectDeviceAction => ({
  type: types.SELECT_DEVICE,
  payload: deviceId,
  meta,
});

export const setUpdateModalVisibility = (visible: boolean, meta: MetaAction = {}): DeviceType.SetUpdateModalVisibilityAction => ({
  type: types.SET_UPDATE_MODAL_VISIBILITY,
  payload: visible,
  meta,
});

export const fetchDevicesAppVersion = (parameters?: Record<string, unknown>, meta: MetaAction = {}): DeviceType.FetchDevicesAppVersionAction => ({
  type: types.FETCH_DEVICES_APP_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: DEVICES_REDUCE_URL,
      params: {
        ...parameters,
        sort: 'lastAppVersion,desc',
        size: MAX_RECORD,
        reducer: ['lastAppVersion,none', 'id,count'],
      },
    },
  },
  meta,
});

export const fetchDevicesAppVersionSuccess = (
  versions: PageContent<DeviceType.DeviceAppVersionBucket>,
  meta: MetaAction = {},
): DeviceType.FetchDevicesAppVersionSuccessAction => ({
  type: types.FETCH_DEVICES_APP_SUCCESS,
  payload: versions,
  meta,
});

export const fetchDevicesAppVersionFailure = (error: ApiError, meta: MetaAction = {}): DeviceType.FetchDevicesAppVersionFailureAction => ({
  type: types.FETCH_DEVICES_APP_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchDevicesOSVersion = (parameters?: Record<string, unknown>, meta: MetaAction = {}): DeviceType.FetchDevicesOSVersionAction => ({
  type: types.FETCH_DEVICES_OS_VERSION_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: DEVICES_REDUCE_URL,
      params: {
        ...parameters,
        sort: 'type,asc;operatingSystemVersion,desc',
        size: MAX_RECORD,
        reducer: ['operatingSystemVersion,none', 'type,none', 'id,count'],
      },
    },
  },
  meta,
});

export const fetchDevicesOSVersionSuccess = (
  data: PageContent<DeviceType.DeviceOSVersionBucket>,
  meta: MetaAction = {},
): DeviceType.FetchDevicesOSVersionSuccessAction => ({
  type: types.FETCH_DEVICES_OS_VERSION_SUCCESS,
  payload: data,
  meta,
});

export const fetchDevicesOSVersionFailure = (error: ApiError, meta: MetaAction = {}): DeviceType.FetchDevicesOSVersionFailureAction => ({
  type: types.FETCH_DEVICES_OS_VERSION_FAILURE,
  payload: error,
  error: true,
  meta,
});
